import React, { useState } from 'react';
import { ArrowBack, Delete, Add, Remove, ShoppingCart } from '@mui/icons-material';
import BankPaymentModal from '../Layout/BankPaymentModal';

const CheckoutModal = ({ open, cart, onClose, onUpdateQuantity, onRemoveItem, onClearCart }) => {
    const [showPayment, setShowPayment] = useState(false);

    if (!open) return null;

    const mediaUrl = process.env.REACT_APP_MEDIA_URL;

    const calculateTotal = () => {
        return cart.reduce((total, item) => total + (item.sellingPrice || 0) * (item.qty || 1), 0);
    };

    const calculateSubtotal = () => calculateTotal();
    const calculateTax = () => calculateTotal() * 0.075; // 7.5% VAT
    const calculateGrandTotal = () => calculateSubtotal() + calculateTax();

    const handleCheckout = () => {
        setShowPayment(true);
    };

    const handlePaymentSuccess = () => {
        setShowPayment(false);
        onClearCart();
        onClose();
        // You can add success notification here
    };

    return (
        <>
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 1100 }}>
                <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
                    {/* Header - Fixed */}
                    <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <button onClick={onClose} className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center hover:bg-green-200 transition-colors">
                            <ArrowBack className="text-green-700" sx={{ fontSize: 20 }} />
                        </button>
                        <div className="flex items-center space-x-2">
                            <ShoppingCart className="text-green-600" sx={{ fontSize: 24 }} />
                            <h2 className="text-lg sm:text-xl font-bold text-gray-900">Shopping Cart</h2>
                        </div>
                        <button
                            onClick={onClearCart}
                            className="text-xs sm:text-sm text-red-600 hover:text-red-800 font-medium"
                        >
                            Clear All
                        </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="overflow-y-auto flex-1 px-4 sm:px-6 py-4">
                        <style>{`
              .overflow-y-auto::-webkit-scrollbar {
                width: 6px;
              }
              .overflow-y-auto::-webkit-scrollbar-track {
                background: #f1f1f1;
                border-radius: 10px;
              }
              .overflow-y-auto::-webkit-scrollbar-thumb {
                background: #888;
                border-radius: 10px;
              }
              .overflow-y-auto::-webkit-scrollbar-thumb:hover {
                background: #555;
              }
            `}</style>

                        {cart.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <ShoppingCart sx={{ fontSize: 80 }} className="text-gray-300 mb-4" />
                                <p className="text-xl font-semibold text-gray-600 mb-2">Your cart is empty</p>
                                <p className="text-sm text-gray-500">Add some items to get started</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {cart.map((item) => {
                                    const imageUrl = item.productPicture
                                        ? `${mediaUrl}/${item.productPicture}`
                                        : item.logoPath
                                            ? `${mediaUrl}/${item.logoPath}`
                                            : null;

                                    return (
                                        <div
                                            key={item.id}
                                            className="flex flex-col sm:flex-row gap-4 p-4 border border-gray-200 rounded-xl hover:shadow-md transition-shadow bg-white"
                                        >
                                            {/* Product Image */}
                                            {imageUrl && (
                                                <div className="flex-shrink-0 w-full sm:w-24 h-24 bg-gray-50 rounded-lg overflow-hidden">
                                                    <img
                                                        src={imageUrl}
                                                        alt={item.productName}
                                                        className="w-full h-full object-contain"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                        }}
                                                    />
                                                </div>
                                            )}

                                            {/* Product Details */}
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-gray-900 text-sm sm:text-base mb-1 line-clamp-2">
                                                    {item.productName}
                                                </h3>
                                                {item.clinicName && (
                                                    <p className="text-xs sm:text-sm text-gray-600 mb-2">
                                                        📍 {item.clinicName}
                                                    </p>
                                                )}
                                                <p className="text-lg font-bold text-green-600">
                                                    ₦{item.sellingPrice?.toLocaleString() || '0'}
                                                </p>
                                            </div>

                                            {/* Quantity Controls */}
                                            <div className="flex sm:flex-col items-center justify-between sm:justify-center gap-2 sm:gap-3">
                                                <div className="flex items-center space-x-2 border border-gray-300 rounded-lg px-2 py-1">
                                                    <button
                                                        onClick={() => onUpdateQuantity(item.id, Math.max(1, (item.qty || 1) - 1))}
                                                        className="text-gray-600 hover:text-green-600 p-1"
                                                    >
                                                        <Remove sx={{ fontSize: 18 }} />
                                                    </button>
                                                    <span className="font-semibold text-gray-900 min-w-[24px] text-center">
                                                        {item.qty || 1}
                                                    </span>
                                                    <button
                                                        onClick={() => onUpdateQuantity(item.id, (item.qty || 1) + 1)}
                                                        className="text-gray-600 hover:text-green-600 p-1"
                                                    >
                                                        <Add sx={{ fontSize: 18 }} />
                                                    </button>
                                                </div>

                                                {/* Subtotal */}
                                                <div className="text-right sm:text-center">
                                                    <p className="text-xs text-gray-500">Subtotal</p>
                                                    <p className="font-bold text-gray-900">
                                                        ₦{((item.sellingPrice || 0) * (item.qty || 1)).toLocaleString()}
                                                    </p>
                                                </div>

                                                {/* Remove Button */}
                                                <button
                                                    onClick={() => onRemoveItem(item.id)}
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                    title="Remove item"
                                                >
                                                    <Delete sx={{ fontSize: 20 }} />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Footer - Fixed (Summary & Checkout) */}
                    {cart.length > 0 && (
                        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 flex-shrink-0 bg-gray-50">
                            {/* Summary */}
                            <div className="space-y-2 mb-4">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Subtotal ({cart.length} items)</span>
                                    <span className="font-semibold text-gray-900">
                                        ₦{calculateSubtotal().toLocaleString()}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">VAT (7.5%)</span>
                                    <span className="font-semibold text-gray-900">
                                        ₦{calculateTax().toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                                <div className="flex justify-between text-base sm:text-lg font-bold border-t pt-2">
                                    <span className="text-gray-900">Total</span>
                                    <span className="text-green-600">
                                        ₦{calculateGrandTotal().toLocaleString(undefined, { maximumFractionDigits: 2 })}
                                    </span>
                                </div>
                            </div>

                            {/* Checkout Button */}
                            <button
                                onClick={handleCheckout}
                                className="w-full bg-green-600 text-white py-3 sm:py-3.5 rounded-xl font-bold text-base sm:text-lg hover:bg-green-700 active:bg-green-800 transition-colors shadow-lg hover:shadow-xl"
                            >
                                Proceed to Payment
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Payment Modal */}
            <BankPaymentModal
                open={showPayment}
                onClose={() => setShowPayment(false)}
                paymentTitle="Pharmacy Order Payment"
                amount={calculateGrandTotal()}
                currency="NGN"
                userEmail={sessionStorage.getItem('email') || ''}
                onPay={handlePaymentSuccess}
                hcProviderType="pharmacy"
                hcProvider={{ email: cart[0]?.clinicContactEmail }}
                service={{
                    productName: cart.length === 1 ? cart[0].productName : `${cart.length} items`,
                    sellingPrice: calculateGrandTotal()
                }}
            />
        </>
    );
};

export default CheckoutModal;
